import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import CodeEditor from "../components/CodeEditor";
import { getToken, logout, getUserId } from "../utils/auth";
import "./SolveQuestionPage.css";
import HumanLoader from "../components/loaders/HumanLoader";

export default function SolveQuestionPage() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [question, setQuestion] = useState(null);
  const [language, setLanguage] = useState("py");
  const [code, setCode] = useState(""); // This will now hold ONLY the target function
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const goBack = () => {
    navigate(-1); 
  };

  const getPredefinedCodeForLanguage = (question, lang) => {
    if (!question?.predefinedCode) return "";
    const found = question.predefinedCode.find(p => p.language === lang);
    return found ? found.code : "";
  };

  const extractTargetFunction = (fullCode, functionName, lang) => {
  if (!fullCode || !functionName) return fullCode;
  const lines = fullCode.split("\n");

  if (lang === "py") {
    const startIndex = lines.findIndex(line => line.trim().startsWith(`def ${functionName}`));
    if (startIndex === -1) return fullCode;
    
    const functionLines = [];
    for (let i = startIndex; i < lines.length; i++) {
      if (lines[i].includes('if __name__ == "__main__":')) break;
      functionLines.push(lines[i]);
    }
    return functionLines.join("\n").trim();
  } else {
    const strictRegex = new RegExp(`(?:bold|long|short|unsigned|signed|int|void|double|float|char|string|bool|auto|std::\\w+)\\s+${functionName}\\s*\\([^)]*\\)\\s*\\{`);
    
    let match = fullCode.match(strictRegex);
    
    // Fallback if they use a custom class/struct name as a return type
    if (!match) {
      const fallbackRegex = new RegExp(`(?:\\w+\\s+)+${functionName}\\s*\\([^)]*\\)\\s*\\{`);
      match = fullCode.match(fallbackRegex);
    }
    
    if (!match) return fullCode;

    let startIndex = match.index;
    
    // 2. Extra safety clean-up: If the matched segment somehow pulled in "Target Function", 
    // slice it off by locating the function name itself and looking backward for the type.
    const matchString = match[0];
    if (matchString.includes("Target Function")) {
      const actualStart = matchString.indexOf(functionName);
      // Back up to catch the return type keyword right before it
      const wordsBefore = matchString.substring(0, actualStart).trim().split(/\s+/);
      const lastWord = wordsBefore[wordsBefore.length - 1]; // This will be 'int', 'void', etc.
      
      const correctedSignatureStart = fullCode.indexOf(lastWord + " " + functionName);
      if (correctedSignatureStart !== -1) {
        startIndex = correctedSignatureStart;
      }
    }

    let braceCount = 0;
    let endIndex = -1;
    let started = false;

    for (let i = startIndex; i < fullCode.length; i++) {
      if (fullCode[i] === "{") {
        braceCount++;
        started = true;
      } else if (fullCode[i] === "}") {
        braceCount--;
      }

      if (started && braceCount === 0) {
        endIndex = i;
        break;
      }
    }

    if (endIndex !== -1) {
      return fullCode.substring(startIndex, endIndex + 1).trim();
    }
  }
  return fullCode;
};

  // Stitches the user's updated function code back into the pristine database template
  const reconstructFullCode = (userFuncCode, question, lang) => {
    const originalTemplate = getPredefinedCodeForLanguage(question, lang);
    const functionName = question.targetFunctionName;
    if (!originalTemplate || !functionName) return userFuncCode;

    if (lang === "py") {
      const lines = originalTemplate.split("\n");
      const startIndex = lines.findIndex(line => line.includes(`def ${functionName}`));
      if (startIndex === -1) return userFuncCode;

      let endIndex = lines.findIndex(line => line.includes('if __name__ == "__main__":'));
      if (endIndex === -1) endIndex = lines.length;

      const before = lines.slice(0, startIndex).join("\n");
      const after = lines.slice(endIndex).join("\n");

      return `${before}\n${userFuncCode}\n\n${after}`;
    } else {
      const regex = new RegExp(`(?:\\w+\\s+)+${functionName}\\s*\\([^)]*\\)\\s*\\{`);
      const match = originalTemplate.match(regex);
      if (!match) return userFuncCode;

      const startIndex = match.index;
      let braceCount = 0;
      let endIndex = -1;
      let started = false;

      for (let i = startIndex; i < originalTemplate.length; i++) {
        if (originalTemplate[i] === "{") {
          braceCount++;
          started = true;
        } else if (originalTemplate[i] === "}") {
          braceCount--;
        }
        if (started && braceCount === 0) {
          endIndex = i;
          break;
        }
      }

      if (endIndex !== -1) {
        const before = originalTemplate.substring(0, startIndex);
        const after = originalTemplate.substring(endIndex + 1);
        return `${before}${userFuncCode}${after}`;
      }
    }
    return userFuncCode;
  };


  /* ---------------- FETCH QUESTION ---------------- */
  useEffect(() => {
    async function fetchQuestion() {
      try {
        const res = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/questions/${id}`,
          {
            headers: {
              Authorization: `Bearer ${getToken()}`
            }
          }
        );

        if (res.status === 401 || res.status === 403) {
          logout();
          navigate("/login");
          return;
        }

        const resu = await res.json();
        setQuestion(resu);
        console.log(resu);
        
      } catch (err) {
        console.error("Failed to load question", err);
      }
    }

    fetchQuestion();
  }, [id, navigate]);

  /* ========== INJECT EXTRACTED TARGET FUNCTION ONLY ========== */
  useEffect(() => {
    if (!question) return;

    const fullStarterCode = getPredefinedCodeForLanguage(question, language);
    
    // If targeted isolation is enabled, parse out the component function
    if (question.isTargetFunction && question.targetFunctionName) {
      const isolatedFunc = extractTargetFunction(fullStarterCode, question.targetFunctionName, language);
      setCode(isolatedFunc || "");
    } else {
      setCode(fullStarterCode || "");
    }
  }, [question, language]);

  /* ---------------- RUN SAMPLE ---------------- */
  const runSample = async () => {
    setLoading(true);
    setResult(null);

    // Reconstruct entire runnable payload before hitting compiler API
    const finalExecutableCode = question.isTargetFunction 
      ? reconstructFullCode(code, question, language)
      : code;

    try {
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/run`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getToken()}`
        },
        body: JSON.stringify({
          language,
          code: finalExecutableCode, // Entire template + updated user logic sent here
          testcases: question.sampleTestcases
        })
      });

      if (res.status === 401 || res.status === 403) {
        logout();
        navigate("/login");
        return;
      }

      setResult(await res.json());
    } catch {
      setResult({ error: "Execution failed" });
    } finally {
      setLoading(false);
    }
  };

  /* ---------------- SUBMIT ---------------- */
  const submitHidden = async () => {
    setLoading(true);
    setResult(null);

    // Reconstruct entire runnable payload before hitting compiler API
    const finalExecutableCode = question.isTargetFunction 
      ? reconstructFullCode(code, question, language)
      : code;

    try {
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/run`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getToken()}`
        },
        body: JSON.stringify({
          language,
          code: finalExecutableCode, // Entire template + updated user logic sent here
          questionId: id,
          studentId: getUserId()
        })
      });

      if (res.status === 401 || res.status === 403) {
        logout();
        navigate("/login");
        return;
      }

      setResult(await res.json());
    } catch {
      setResult({ error: "Submission failed" });
    } finally {
      setLoading(false);
    }
  };

  if (!question) {
    return (
      <HumanLoader
        loadingText="Preparing your problem"
        successText="Ready to practice!"
        duration={2000}
      />
    );
  }

  return (
    <div className="compiler-root">
      <div className="page-toolbar">
        <button className="go-back-button" onClick={goBack}>
          ← Go Back
        </button>
      </div>
      <div className="solve-layout">

        {/* LEFT PANEL */}
        <div className="solve-left">
          <div className="question-panel">

            <div className="question-header">
              <h1>{question.title}</h1>
              <span className={`difficulty ${question.difficulty?.toLowerCase()}`}>
                {question.difficulty}
              </span>
            </div>

            <div
              className="question-description"
              dangerouslySetInnerHTML={{ __html: question.description }}
            />

            <h3 className="section-heading">Sample Testcases</h3>
            <p className="sample-intro">
              Use the sample testcases to verify your logic. Input and output are shown below for quick reference.
            </p>

            <div className="sample-table-wrapper">
              <table className="sample-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Input</th>
                    <th>Output</th>
                  </tr>
                </thead>
                <tbody>
                  {question.sampleTestcases.map((tc, i) => (
                    <tr key={i}>
                      <td>#{i + 1}</td>
                      <td>
                        <pre>{tc.input}</pre>
                      </td>
                      <td>
                        <pre>{tc.output}</pre>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

          </div>
        </div>

        {/* RIGHT PANEL */}
        <div className="solve-right">
          <div className="editor-shell">

            <div className="editor-top-bar">
              <div className="language-selector">
                <select
                  value={language}
                  onChange={(e) => setLanguage(e.target.value)}
                >
                  <option value="py">Python</option>
                  <option value="c">C</option>
                  <option value="cpp">C++</option>
                  <option value="java">Java</option>
                </select>
              </div>
            </div>

            <CodeEditor
              language={language}
              code={code}
              setCode={setCode}
            />

            <div className="action-bar">
              <button onClick={runSample} disabled={loading}>
                ▶ Run
              </button>
              <button
                className="submit"
                onClick={submitHidden}
                disabled={loading}
              >
                Submit
              </button>
            </div>

          </div>
        </div>
      </div>

      {/* RESULT */}
      {result && (
        <div className="result-panel">
          <div className="result-summary">
            <div className="summary-left">
              <span
                className={`summary-verdict ${
                  result.verdict === "AC" ? "ok" : "fail"
                }`}
              >
                {result.verdict === "AC" ? "✔ Accepted" : "✖ Wrong Answer"}
              </span>

              <span className="summary-count">
                {result.passed} / {result.total} testcases passed
              </span>
            </div>

            {result.submissionId && (
              <span className="summary-tag">Submission saved</span>
            )}
          </div>

          {result.submissionId === null && result.results && (
            <div className="testcase-list">
              {result.results.map((tc, i) => {
                const isError =
                  tc.status !== "PASS" &&
                  (tc.actual?.includes("error") ||
                    tc.actual?.includes("Error") ||
                    tc.actual?.includes("Exception") ||
                    tc.actual?.includes("In function"));

                return (
                  <div key={i}>
                    {!isError && (
                      <div
                        className={`testcase-card ${
                          tc.status === "PASS" ? "tc-pass" : "tc-fail"
                        }`}
                      >
                        <div className="tc-header">
                          <span>Testcase #{i + 1}</span>
                          <span className="tc-status">{tc.status}</span>
                        </div>

                        <div className="tc-row">
                          <label>Input</label>
                          <pre>{tc.input}</pre>
                        </div>

                        <div className="tc-row">
                          <label>Expected</label>
                          <pre>{tc.expected}</pre>
                        </div>

                        <div className="tc-row">
                          <label>Your Output</label>
                          <pre>{tc.actual}</pre>
                        </div>
                      </div>
                    )}

                    {isError && (
                      <div className="error-card">
                        <div className="error-header">
                          🚨 Error in Testcase #{i + 1}
                          <span className="error-tag">{tc.status}</span>
                        </div>
                        <pre className="error-output">{tc.actual}</pre>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}